import {debounceTime, ReplaySubject, takeUntil, tap} from 'rxjs';
import {Injectable, OnDestroy} from '@angular/core';

import {
  TrackedEvent,
  EventTrackingStore,
  EventStatus,
} from '@stores/event-tracking.store';
import {AiTasksStore} from '@stores/ai-tasks.store';
import {NotificationStore} from '@stores/notification.store';

import * as ErrU from '@common/utils/dist/utils/error-utils.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';
import * as APIM from '@common/utils/dist/models/api-messages.js';

import {
  lessonNotAvailableNotificationContent,
  permissionNotificationContent,
} from '@constants/app.config';

import {getIndexQueryKey} from '@utils/event-utils';

@Injectable({
  providedIn: 'root',
})
export class TrackedEventReactor implements OnDestroy {
  private _destroyed = new ReplaySubject<void>();

  /**
   * 6 minutes 15 seconds is 15 seconds beyond the maximun service side timeout
   * so if a timeout error is on the way we will deal with it that way
   */
  private _maxInactivePending =
    6 * U.duration.minutes + 15 * U.duration.seconds;
  private _maxInactiveComplete = 30 * U.duration.seconds;

  private _debounceTime = 1 * U.duration.seconds; // Debounce time in milliseconds

  constructor(
    private eventTrackingStore: EventTrackingStore,
    private notificationStore: NotificationStore,
    private aiTasksStore: AiTasksStore,
  ) {
    this.eventTrackingStore.state$
      .pipe(
        debounceTime(this._debounceTime), // Debounce to avoid too frequent checks
        tap((state) => {
          Object.entries(state).forEach(([id, ae]) => {
            if (U.isEmpty(ae)) {
              return;
            }
            switch (ae.status) {
              case EventStatus.Pending:
                if (this._hasExpired(ae, this._maxInactivePending)) {
                  this.eventTrackingStore.upsertEvent(id, EventStatus.Failure, {
                    ...(ae.event ?? <any>{}),
                    err: ErrU.timeoutError,
                  });
                }
                break;
              case EventStatus.Success:
                this._removeExpiredEvent(id, ae);
                break;
              case EventStatus.Failure:
                this._notifyError(ae);
                this._removeExpiredEvent(id, ae);
                break;
            }
          });
        }),
        takeUntil(this._destroyed),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  private _hasExpired(event: TrackedEvent, maxTime: number): boolean {
    return event.ts < Date.now() - maxTime;
  }

  /**
   * AI scores the teacher has been shown but has not accepted or discarded yet. Their
   * results are kept on the AI task (`draft`), but the event keeps the status of the
   * run the AI autoscoring panel shows, so it never expires while the review is open.
   * Approving or clearing the scores in the panel clears the task, and that is what lets
   * these events be reclaimed.
   */
  private _isUnderReview(id: string, event: TrackedEvent): boolean {
    const outbound = event.outbound as {eventType?: APIM.EventIntent};
    return (
      event.status === EventStatus.Success &&
      outbound?.eventType === APIM.EventIntent.WSSAutoScore &&
      this.aiTasksStore.isTracked(id)
    );
  }

  private _removeExpiredEvent(id: string, ae: TrackedEvent): void {
    if (this._isUnderReview(id, ae)) {
      return;
    }
    if (this._hasExpired(ae, this._maxInactiveComplete)) {
      this.eventTrackingStore.removeEvent(id);
    }
  }

  private _notifyError(event: TrackedEvent): void {
    const code = event.event.err?.code;
    const pK = getIndexQueryKey(event.outbound);
    if (U.isEmpty(pK)) {
      return;
    }
    switch (code) {
      case 403:
      case 401:
        this.notificationStore.add(pK, {
          content: permissionNotificationContent,
        });
        break;

      case 404:
        if (event.outbound?.c?.[0]?.idx === APIM.QueryType.SourceIndex) {
          this.notificationStore.add(pK, {
            content: lessonNotAvailableNotificationContent,
          });
        }
        break;
      default:
        return;
    }
  }
}
